"use client";

import { useState } from "react";
import type { YearValue } from "@/lib/data/types";
import { HistoricalLineChart } from "@/components/charts/historical-line-chart";
import { RangeSelector, type ChartRangeLabel } from "@/components/charts/range-selector";
import { filterYearRange } from "@/lib/year-range";
import { describeTrend } from "@/lib/trend-summary";
import { medianOf } from "@/lib/stats";
import { formatCompactCurrency, formatCompactNumber, formatPercent, formatRatio } from "@/lib/format";
import { cn } from "@/lib/utils";

type HistoricalChartFormat = "ratio" | "percent" | "currency" | "number";

function formatMedianValue(format: HistoricalChartFormat, value: number, currency: string): string {
  if (format === "percent") return formatPercent(value);
  if (format === "currency") return formatCompactCurrency(value, currency);
  if (format === "number") return formatCompactNumber(value);
  return formatRatio(value);
}

export function RangedHistoricalChart({
  title,
  data,
  showMedian = false,
  format,
  currency = "USD",
  color,
  variant = "area",
  showTitle = true,
}: {
  title: string;
  data: YearValue[];
  /** Zeigt eine Median-Referenzlinie, die aus der aktuell GEWAEHLTEN Zeitspanne neu
   * berechnet wird (Feedback-Runde 19 Punkt 6: der Median war vorher fest aus der
   * kompletten Historie berechnet und reagierte nicht auf den RangeSelector). */
  showMedian?: boolean;
  format: HistoricalChartFormat;
  currency?: string;
  color?: string;
  variant?: "area" | "bar";
  /** false, wenn eine umgebende Komponente (z.B. ChartCard im Kachel-Layout, siehe
   * backlog.md -> §A) den Titel bereits selbst anzeigt - vermeidet eine doppelte
   * Ueberschrift, der RangeSelector bleibt trotzdem sichtbar. */
  showTitle?: boolean;
}) {
  const [range, setRange] = useState<ChartRangeLabel>("Max");
  const rangedData = filterYearRange(data, range);
  const rangeMedian = showMedian ? medianOf(rangedData) : undefined;

  // Trendsatz bewusst aus der VOLLEN Historie (nicht der aktuell gewaehlten
  // 1J/3J/5J/10J-Ansicht) - Erik will die langfristige Entwicklung ab 5-10 Jahren
  // beschrieben haben, unabhaengig davon, welchen Zeitraum der Nutzer gerade
  // eingestellt hat.
  const trend = describeTrend(title, data, format, currency);

  return (
    <div>
      <div className={cn("mb-3 flex items-center gap-4", showTitle ? "justify-between" : "justify-end")}>
        {showTitle && <h3 className="text-sm font-medium text-ink-secondary">{title}</h3>}
        <RangeSelector value={range} onChange={setRange} />
      </div>
      <HistoricalLineChart
        data={rangedData}
        referenceValue={rangeMedian}
        referenceLabel={rangeMedian !== undefined ? `Median ${formatMedianValue(format, rangeMedian, currency)}` : undefined}
        format={format}
        currency={currency}
        color={color}
        variant={variant}
        // Kurzform ohne das "im Zeitverlauf"-Suffix (falls vorhanden) - im Tooltip an
        // einem einzelnen Datenpunkt wirkt der volle Kachel-Titel redundant, der Name der
        // Kennzahl allein reicht (Erik 2026-07-22: KPI-Name statt generisch "Wert").
        valueLabel={title.replace(/ im Zeitverlauf$/, "")}
      />
      {trend && <p className="mt-2 text-xs text-muted-foreground">{trend}</p>}
    </div>
  );
}
